
import { api } from './base';
import { cachedFetch, invalidateCached } from '@/shared/lib/refCache';
import type {
  Category,
  Subcategory,
  CreateCategoryDto,
  UpdateCategoryDto,
  CreateSubcategoryDto,
  UpdateSubcategoryDto,
} from '@/entities/category';

const CATALOG_TTL_MS = 5 * 60_000;

function invalidateCatalog<T>(res: T): T {
  invalidateCached('catalog:');
  return res;
}

export const categoriesApi = {
  list: () => cachedFetch('catalog:list', CATALOG_TTL_MS, () => api.get<Category[]>('/api/v1/categories')),

  listAll: () => api.get<Category[]>('/api/v1/categories?all=1'),

  listForAdmin: () => api.get<Category[]>('/api/v1/categories?includeArchived=1'),

  catalogCounts: () =>
    api.get<{ categories: Record<string, number>; subcategories: Record<string, number> }>(
      '/api/v1/categories/catalog-counts',
    ),

  create: (dto: CreateCategoryDto) =>
    api.post<Category>('/api/v1/categories', dto).then(invalidateCatalog),
  update: (id: string, dto: UpdateCategoryDto) =>
    api.patch<Category>(`/api/v1/categories/${id}`, dto).then(invalidateCatalog),
  delete: (id: string) => api.delete<unknown>(`/api/v1/categories/${id}`).then(invalidateCatalog),

  setArchived: (id: string, archived: boolean) =>
    api
      .put<{ ok: true }>(`/api/v1/categories/${id}/archived`, { archived })
      .then(invalidateCatalog),

  setSubcategoryArchived: (id: string, archived: boolean) =>
    api
      .put<{ ok: true }>(`/api/v1/subcategories/${id}/archived`, { archived })
      .then(invalidateCatalog),

  getSubcategories: (categoryId: string) =>
    api.get<Subcategory[]>(`/api/v1/categories/${categoryId}/subcategories`),
  getSubcategory: (id: string) => api.get<Subcategory>(`/api/v1/subcategories/${id}`),
  createSubcategory: (categoryId: string, dto: CreateSubcategoryDto) =>
    api.post<Subcategory>(`/api/v1/categories/${categoryId}/subcategories`, dto).then(invalidateCatalog),
  updateSubcategory: (id: string, dto: UpdateSubcategoryDto) =>
    api.patch<Subcategory>(`/api/v1/subcategories/${id}`, dto).then(invalidateCatalog),
  deleteSubcategory: (id: string) => api.delete<unknown>(`/api/v1/subcategories/${id}`).then(invalidateCatalog),
};

export interface FavoritesPayload {
  categoryIds: string[];
  subcategoryIds: string[];
}

type FavoriteTarget = { categoryId: string } | { subcategoryId: string };

function favoriteQuery(target: FavoriteTarget): string {
  const [key, value] = Object.entries(target)[0];
  return `?${key}=${encodeURIComponent(value)}`;
}

export const favoritesApi = {
  list: () => api.get<FavoritesPayload>('/api/v1/favorites'),
  add: (target: FavoriteTarget) => api.put<unknown>('/api/v1/favorites', target),
  remove: (target: FavoriteTarget) =>
    api.delete<unknown>(`/api/v1/favorites${favoriteQuery(target)}`),
};

export type FeedbackKind = 'bug' | 'improvement';

export interface FeedbackSettings {
  bugSubcategoryId: string | null;
  improvementSubcategoryId: string | null;
}

export const feedbackSettingsApi = {
  get: () => api.get<FeedbackSettings>('/api/v1/admin/feedback-settings'),
  update: (dto: Partial<FeedbackSettings>) =>
    api.put<FeedbackSettings>('/api/v1/admin/feedback-settings', dto),
};
